import {
  Checkbox,
  Menu,
  MenuItem,
  MenuList,
  Paragraph,
  Stack,
  Tag
} from '../../atoms';
import { TagMultiSelectButton } from './components/tag-multi-select-button';

export type TagMultiSelectOptionType = {
  label: string;
  value: string;
};

type Props = {
  options: TagMultiSelectOptionType[];
  selectedValues: string[];
  onChangeAll: () => void;
  onChangeItems: (value: string) => void;
  noValuesLabel: string;
  allValuesLabel: string;
  allOptionsLabel: string;
  tagSizeLimit?: number;
};

export function TagMultiSelect({
  noValuesLabel = 'Select',
  allValuesLabel = 'All',
  allOptionsLabel = 'All',
  selectedValues,
  options,
  onChangeAll,
  onChangeItems,
  tagSizeLimit = 10000
}: Props): JSX.Element {
  return (
    <Menu closeOnSelect={false}>
      {({ isOpen }): JSX.Element => (
        <>
          <TagMultiSelectButton
            textAlign="left"
            isOpen={isOpen}
            whiteSpace={selectedValues.length > 3 ? 'normal' : 'inherit'}
          >
            {getInputContent({
              selectedValues,
              allValuesLabel,
              noValuesLabel,
              options,
              tagSizeLimit
            })}
          </TagMultiSelectButton>
          <MenuList maxHeight="300px" overflowY="auto">
            <MenuItem>
              <Checkbox
                w="100%"
                isChecked={selectedValues.length > 0}
                isIndeterminate={
                  selectedValues.length > 0 &&
                  selectedValues.length !== options.length
                }
                onChange={(e): void => {
                  e.preventDefault();
                  onChangeAll();
                }}
              >
                {allOptionsLabel}
              </Checkbox>
            </MenuItem>
            <Stack spacing={1}>
              {options.map((option) => (
                <MenuItem
                  key={option.value}
                  onClick={(e): void => {
                    e.stopPropagation();
                  }}
                  pl="36px"
                >
                  <Checkbox
                    w="100%"
                    isChecked={selectedValues.includes(option.value)}
                    onChange={(): void => onChangeItems(option.value)}
                  >
                    {option.label}
                  </Checkbox>
                </MenuItem>
              ))}
            </Stack>
          </MenuList>
        </>
      )}
    </Menu>
  );
}

const getInputContent = ({
  allValuesLabel,
  noValuesLabel,
  options,
  selectedValues,
  tagSizeLimit
}: {
  selectedValues: string[];
  noValuesLabel: string;
  options: TagMultiSelectOptionType[];
  allValuesLabel: string;
  tagSizeLimit: number;
}): JSX.Element => {
  const hiddenTagsCount = selectedValues.length - tagSizeLimit;
  const shouldShowHiddenTagsCounter = selectedValues.length > tagSizeLimit;
  const noValuesSelected = selectedValues.length === 0;
  const allValuesSelected = selectedValues.length === options.length;

  if (noValuesSelected) {
    return (
      <Paragraph fontWeight="normal" color="palette_gray.100">
        {noValuesLabel}
      </Paragraph>
    );
  } else if (allValuesSelected) {
    return (
      <Paragraph fontWeight="normal" color="palette_gray.100">
        {allValuesLabel}
      </Paragraph>
    );
  } else if (
    selectedValues.length > 0 &&
    selectedValues.length !== options.length
  ) {
    return (
      <>
        {selectedValues.slice(0, tagSizeLimit).map((value) => (
          <Tag
            py="space_unit.x1"
            fontWeight={400}
            backgroundColor="palette_gray.0"
            color="palette_gray.100"
            mr="space_unit.x1"
            key={value}
          >
            {options.find((o) => o.value === value)?.label}
          </Tag>
        ))}
        {shouldShowHiddenTagsCounter && (
          <Tag
            py="space_unit.x1"
            fontWeight={400}
            backgroundColor="palette_gray.0"
            color="palette_gray.100"
            mr="space_unit.x1"
          >
            {`+${hiddenTagsCount}`}
          </Tag>
        )}
      </>
    );
  }
  return <></>;
};
